import React from 'react';
import styles from "./FormFields.module.css";
import { Checkbox } from "../Checkbox/index";
import type { LandingPageTemplateResponse } from "@/types/landing";
import {
    FormFieldAttributes,
    FormFieldResponse,
    SignInProvider,
} from "@/types/landing";
import { StrapiEntity } from "@/types/strapi";
import {
    LeadFormButtonVariant,
    CallToActionButton,
} from "../CallToActionButton";
import { Fragment, ReactElement } from "react";
import { debugError } from "@/utils/logs";
import { SocialSignUpGroup } from "../SocialSignUpGroup";
import { SkipLeadForm } from "@/components/landing/SkipLeadForm";
import type { Site } from "@/types/sites";
import { Input } from "./Fields/Input";
import {
    FormVariant,
    getVariantConfig,
    VariantConfig,
} from "@/contexts/PageContext/lib/theming/variants";
import { usePageClientContext } from "@/contexts/PageContext/client";
import { StateDropdown } from '@/components/common/StateDropdown/StateDropdown';
import { useUserInfo } from '@/contexts/UserInfoContext';

type FormProps = ({
    formFields,
    buttonText,
    providers,
    skipButtonText,
    fromPopup,
    onOfferPage,
    onStateChange,
}: {
    formFields: FormFieldResponse;
    buttonText?: string;
    providers: SignInProvider[];
    skipButtonText?: string;
    fromPopup?: boolean;
    site: Site | null;
    landingPage: LandingPageTemplateResponse | null;
    onOfferPage?: boolean;
    isLoading: boolean;
    onStateChange?: (state: string) => void;
}) => React.ReactElement;

export const SKIP_BUTTON_VALUE = "skip";

enum FieldType {
    Checkbox = "checkbox",
    AutoOptIn = "auto opt-in",
    Email = "email",
    Tel = "tel",
    Text = "text",
    States = "states"
}

const checkboxTypes = [FieldType.Checkbox, FieldType.AutoOptIn];

export const FormFields: FormProps = ({
    formFields,
    buttonText,
    providers,
    skipButtonText,
    fromPopup,
    site,
    landingPage,
    onOfferPage,
    isLoading,
    onStateChange,
}): ReactElement => {
    const pageContext = usePageClientContext();
    const variantConfig = getVariantConfig(pageContext?.pageType);
    const [selectedState, setSelectedState] = React.useState<string>("");

    const handleStateChange = (state: string) => {
        setSelectedState(state);
        onStateChange?.(state);
    };

    const lastIndex = formFields?.data?.length ? formFields.data.length - 1 : 0;
    buttonText = buttonText ?? "Submit";
    const fullWidthSubmitButton =
        landingPage?.data?.attributes?.fullWidthSubmitButton ||
        landingPage === null;

    const isInPopupAndNotBannerflow = Boolean(
        landingPage?.data?.attributes?.spinner?.data?.attributes || onOfferPage
    );

    const lastInputIndex = (() => {
        const data = formFields?.data ?? [];
        for (let i = data.length - 1; i >= 0; i--) {
            if (
                !checkboxTypes.includes(
                    data[i].attributes.fieldType as FieldType
                )
            ) {
                return i;
            }
        }
        return 0;
    })();

    // Check if the last non-checkbox field is a state dropdown
    const lastNonCheckboxField = (formFields?.data ?? []).findLast(
        (field: StrapiEntity<FormFieldAttributes>) =>
            !checkboxTypes.includes(field.attributes.fieldType as FieldType)
    );
    
    const isLastFieldStateDropdown = lastNonCheckboxField?.attributes.fieldType === FieldType.States;

    // Force full width if landingPage setting is true OR if last non-checkbox field is state dropdown
    const shouldUseFullWidthButton = fullWidthSubmitButton || isLastFieldStateDropdown;

    const firstCheckboxIndex = formFields?.data?.findIndex(
        (field: StrapiEntity<FormFieldAttributes>) =>
            checkboxTypes.includes(field.attributes.fieldType as FieldType)
    );

    const getCheckboxWrapperStyles = (index: number) => {
        if (index === firstCheckboxIndex) {
            return styles.firstCheckboxWrapper;
        }
        return styles.checkboxWrapper;
    };

    return (
        <div
            className={
                variantConfig.form.variant == FormVariant.FULLWIDTH
                    ? styles.fullWidthForm
                    : styles.form
            }
        >
            <SocialSignUpGroup
                signUpProviderGroup={providers}
                fromPopup={fromPopup}
                site={site}
                landingPage={landingPage}
                onOfferPage={onOfferPage}
            />

            {formFields?.data &&
                formFields?.data.length > 0 &&
                formFields.data?.map((field, fieldIndex) => {
                    return (
                        <div
                            key={field.id}
                            className={
                                field.attributes.fieldType === FieldType.States
                                    ? ""
                                    : checkboxTypes.includes(
                                        field.attributes.fieldType as FieldType
                                    )
                                    ? getCheckboxWrapperStyles(fieldIndex)
                                    : styles.formWrapper
                            }
                            data-field-container={field.id}
                        >       
                            {field.attributes.bqMapping.map((bqMapping, bqMappingIndex) => {
                                return (
                                    <Fragment key={`${field.id}_${bqMappingIndex}`}>
                                        {bqMappingIndex === 0 ? (
                                            field.attributes.fieldType === FieldType.States ? (
                                                <StateDropdown 
                                                    value={selectedState} 
                                                    onChange={handleStateChange} 
                                                />
                                            ) : (
                                                getStandardInput(
                                                    field,
                                                    bqMapping,
                                                    fieldIndex,
                                                    lastIndex,
                                                    lastInputIndex,
                                                    buttonText,
                                                    isInPopupAndNotBannerflow,
                                                    shouldUseFullWidthButton,
                                                    isLoading,
                                                    variantConfig
                                                )
                                            )
                                        ) : (
                                            getHiddenInput(
                                                bqMapping,
                                                field.attributes.fieldType === FieldType.AutoOptIn
                                            )
                                        )}
                                    </Fragment>
                                );
                            })}
                        </div>
                    );
                })}

            {shouldUseFullWidthButton ? (
                <CallToActionButton
                    buttonText={buttonText}
                    variant={LeadFormButtonVariant.fullWidthWithFields}
                    isLoading={isLoading}
                />
            ) : null}
            {skipButtonText ? (
                <SkipLeadForm
                    skipButtonText={skipButtonText}
                    isInPopupAndNotBannerflow={isInPopupAndNotBannerflow}
                    site={site}
                    landingPage={landingPage}
                />
            ) : null}
        </div>
    );
};

const getStandardInput = (
    field: StrapiEntity<FormFieldAttributes>,
    bqMapping: string,
    index: number,
    lastIndex: number,
    lastInputIndex: number,
    buttonText: string,
    isInPopupAndNotBannerflow: boolean,
    fullWidthSubmitButton: boolean,
    isLoading: boolean,
    variantConfig: VariantConfig
): ReactElement => {
  
    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const input = e.target;
        let inputParent = input.parentElement;
        while (!inputParent?.dataset?.fieldContainer) {
            inputParent = inputParent ? inputParent.parentElement : null;
            if (!inputParent || inputParent.tagName === "BODY") {
                const errorMsg =
                    "Could not find field container, will not update hidden inputs";
                debugError(errorMsg, input, inputParent);
                throw new Error(errorMsg);
            }
        }
        const hiddenElements =
            inputParent?.querySelectorAll<HTMLInputElement>(
                "input[type=hidden]"
            );
        hiddenElements?.forEach((hiddenElement) => {
            const hiddenValue = ["checkbox", "radio"].includes(input.type)
                ? input.checked
                    ? "on"
                    : undefined
                : input.value;
            if (hiddenValue === undefined) {
                hiddenElement.removeAttribute("value");
            } else {
                hiddenElement.value = hiddenValue;
            }
        });
    };
    return (
        <>
            {/* the default value for the checkbox is `on`.

            If a checkbox is unchecked when its form is submitted, there is no value submitted to
            the server to represent its unchecked state (e.g. value=unchecked); the value is not
            submitted to the server at all. If you wanted to submit a default value for the
            checkbox when it is unchecked, you could include an <input type="hidden"> inside
            the form with the same name and value, generated by JavaScript perhaps.

            https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/checkbox

            e.g.

            <input
                type="hidden"
                name={field.attributes.bqMapping}
                value={JSON.stringify("off")}
            /> */}
            {(checkboxTypes.includes(field.attributes.fieldType as FieldType) &&
                field.attributes.sanitisedFieldText) ||
            field?.attributes?.fieldText ? (
                <Checkbox
                    name={bqMapping}
                    htmlText={
                        field?.attributes?.sanitisedFieldText ||
                        field?.attributes?.fieldText ||
                        ""
                    }
                    required={field?.attributes?.isRequired}
                    changeCallback={onChange}
                    autoChecked={
                        field?.attributes?.fieldType === FieldType.AutoOptIn
                    }
                    style={{ fontFamily: variantConfig.form.font }}
                />
            ) : (
                <Input
                    variant={variantConfig.input}
                    attributes={field.attributes}
                    lastInputIndex={lastInputIndex}
                    onChange={onChange}
                    bqMapping={bqMapping}
                    styles={styles}
                    isInPopupAndNotBannerflow={isInPopupAndNotBannerflow}
                    index={index}
                />
            )}
            {!fullWidthSubmitButton &&
            (lastInputIndex === index ||
                (lastInputIndex === -1 && index === lastIndex)) ? (
                <CallToActionButton
                    buttonText={buttonText}
                    isLoading={isLoading}
                    variant={
                        lastInputIndex > -1
                            ? LeadFormButtonVariant.inlineWithFields
                            : // If the index is -1, then there was no field that was appropriate for the inline button
                              LeadFormButtonVariant.fullWidthWithFields
                    }
                />
            ) : null}
        </>
    );
};

const getHiddenInput = (
    bqMapping: string,
    autoChecked: boolean
): ReactElement => {
    return (
        <input
            name={bqMapping}
            type="hidden"
            value={autoChecked ? "on" : undefined}
        />
    );
};
